import React, { useState } from "react";
import { X, ArrowRightLeft } from "lucide-react";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { profileService, LegalEntityHierarchy } from "@/services/profileService";

interface Props {
  callerEmail: string;
  type: "LEGAL_ENTITY" | "BRANCH";
  selectedIds: string[];
  legalEntities: LegalEntityHierarchy[];
  open: boolean;
  onClose: () => void;
  onMoved: () => void;
}

export const MoveEntityDialog: React.FC<Props> = ({ 
  callerEmail, 
  type,
  selectedIds,
  legalEntities,
  open, 
  onClose, 
  onMoved 
}) => {
  const [targetParentId, setTargetParentId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setTargetParentId("");
    setError(null);
    onClose();
  };

  const { onOverlayClick } = useModalDismiss(open, handleClose);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetParentId) return setError("Please select a target parent");
    
    setSubmitting(true);
    try {
      await profileService.moveEntities(callerEmail, type, selectedIds, targetParentId);
      onMoved();
      handleClose();
    } catch (err: any) {
      setError(err?.message ?? "Failed to move entities");
    } finally {
      setSubmitting(false);
    }
  };

  const entitiesToMove = selectedIds.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onOverlayClick}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-bridge-border/60">
        <div className="flex items-center justify-between px-6 py-4 border-b border-bridge-border/60">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <ArrowRightLeft size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-bridge-fg">Move {type === "BRANCH" ? "Branch" : "Legal Entities"}</h2>
              <p className="text-xs text-bridge-muted mt-0.5">
                Moving {entitiesToMove} {type === "BRANCH" ? "branch" : entitiesToMove === 1 ? "legal entity" : "legal entities"}.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-slate-50 text-bridge-muted hover:text-bridge-fg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-2xl flex items-center gap-3">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              {error}
            </div>
          )}

          <div className="space-y-3">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
              Select Target {type === "BRANCH" ? "Legal Entity" : "Parent"}
            </label>
            <div className="relative group">
              <select
                value={targetParentId}
                onChange={(e) => setTargetParentId(e.target.value)}
                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 hover:border-blue-200 focus:border-blue-500 rounded-2xl text-sm text-slate-800 font-medium outline-none appearance-none transition-all cursor-pointer"
              >
                <option value="">Choose a destination...</option>
                {flattenEntities(legalEntities)
                  .filter(le => !selectedIds.includes(le.id))
                  .map((le) => (
                    <option key={le.id} value={le.id} className="py-2">
                      {"\u00A0\u00A0\u00A0".repeat(le.depth)} {le.name}
                    </option>
                  ))}
              </select>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-blue-500 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 pt-4">
            <button type="button" onClick={handleClose}
              className="px-6 py-3 text-sm font-bold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 border-2 border-transparent rounded-2xl transition-all">
              Cancel
            </button>
            <button type="submit" disabled={submitting || !targetParentId}
              className="px-8 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 active:scale-95 rounded-2xl shadow-lg shadow-blue-200 disabled:opacity-50 disabled:scale-100 disabled:shadow-none transition-all">
              {submitting ? "Moving..." : "Confirm Move"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function flattenEntities(entities: LegalEntityHierarchy[], depth = 0): (LegalEntityHierarchy & { depth: number })[] {
  return entities.reduce((acc, le) => {
    acc.push({ ...le, depth });
    if (le.subEntities && le.subEntities.length > 0) {
      acc.push(...flattenEntities(le.subEntities, depth + 1));
    }
    return acc;
  }, [] as (LegalEntityHierarchy & { depth: number })[]);
}
